import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import AppModal from "components/Modal";
import { Button, FormGroup, Label, Alert, Row, Col, Card, CardBody, Form } from "reactstrap";
import steem from "steem";
import { PrivateKey } from "dsteem";
import { useToast } from "components/ui/use-toast";
import { useAuth } from "helpers/authContext";
import { packSessionData } from "helpers/localStorage";
import { 
  get_signup_captcha, 
  create_signup_order, 
  validate_signup_code, 
  resend_signup_code, 
  cancel_signup_order 
} from "services/db-service";

type Props = {
  isShowing: boolean;
  hide: () => void;
};

const validateSteemUsername = (name: string) => {
  if (!name) return false;
  if (name.length < 3 || name.length > 16) return false;
  
  const segments = name.split('.');
  for (let segment of segments) {
    if (segment.length < 3) return false;
    if (!/^[a-z]/.test(segment)) return false;
    if (!/^[a-z0-9-]+$/.test(segment)) return false;
    if (segment.endsWith('-')) return false;
    if (segment.includes('--')) return false;
  }
  return true;
};

export const SignupModal = ({ isShowing, hide }: Props) => {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [email, setEmail] = useState("");
  const [generatedKeys, setGeneratedKeys] = useState<any>(null);
  const [masterKeyRaw, setMasterKeyRaw] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [hasDownloadedKeys, setHasDownloadedKeys] = useState(false);
  
  const { toast } = useToast();
  const { login } = useAuth() as any;
  
  const [validationCode, setValidationCode] = useState("");
  const [resendTimer, setResendTimer] = useState(60);

  const [focusUser, setFocusUser] = useState(false);
  const [focusEmail, setFocusEmail] = useState(false);
  const [focusCode, setFocusCode] = useState(false);
  const [focusCaptcha, setFocusCaptcha] = useState(false);

  const [captchaData, setCaptchaData] = useState<{id: string, svg: string} | null>(null);
  const [captchaText, setCaptchaText] = useState("");
  const [configuring, setConfiguring] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("steem_signup_order");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.step) setStep(parsed.step);
        if (parsed.username) setUsername(parsed.username || "");
        if (parsed.email) setEmail(parsed.email || "");
        if (parsed.generatedKeys) setGeneratedKeys(parsed.generatedKeys);
        if (parsed.masterKeyRaw) setMasterKeyRaw(parsed.masterKeyRaw || "");
        if (parsed.hasDownloadedKeys !== undefined) setHasDownloadedKeys(parsed.hasDownloadedKeys);
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    if (step < 3) {
      const stateToSave = {
        step, username, email, generatedKeys, masterKeyRaw, hasDownloadedKeys
      };
      localStorage.setItem("steem_signup_order", JSON.stringify(stateToSave));
    } else if (step === 3) {
      localStorage.removeItem("steem_signup_order");
    }
  }, [step, username, email, generatedKeys, masterKeyRaw, hasDownloadedKeys]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (step === 2 && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(t => t - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, resendTimer]);

  useEffect(() => {
    let active = true;
    if (username.length === 0) {
      setUsernameAvailable(null);
      return;
    }
    if (!validateSteemUsername(username)) {
      setUsernameAvailable(false);
      setCheckingUsername(false);
      return;
    }
    setCheckingUsername(true);
    setUsernameAvailable(null);
    setGeneratedKeys(null); 
    
    const timeoutId = setTimeout(async () => {
      try {
        const [ac] = await steem.api.getAccountsAsync([username]);
        if (active) {
          if (ac) setUsernameAvailable(false);
          else setUsernameAvailable(true);
        }
      } catch (e) {
        if (active) setUsernameAvailable(null);
      } finally {
        if (active) setCheckingUsername(false);
      }
    }, 500);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [username]);

  const fetchCaptcha = async () => {
    try {
      const data = await get_signup_captcha();
      setCaptchaData(data);
      setCaptchaText("");
    } catch (e) {
      console.error("Failed to fetch captcha", e);
    }
  };

  useEffect(() => {
    if (isShowing && (step === 1 || step === 2)) {
      fetchCaptcha();
    }
  }, [isShowing, step]);

  const handleGenerateKeys = () => {
    if (!username || usernameAvailable !== true) {
      toast({ title: "Warning", description: "Please choose a valid available username first.", variant: "rose_red" });
      return;
    }
    const array = new Uint32Array(10);
    window.crypto.getRandomValues(array);
    const password = "P" + PrivateKey.fromSeed(array as any).toString();
    setMasterKeyRaw(password);
    
    const keys: any = {};
    const roles: ('owner' | 'active' | 'posting' | 'memo')[] = ['owner', 'active', 'posting', 'memo'];
    roles.forEach((role) => {
      keys[role] = PrivateKey.fromLogin(username, password, role).toString();
      keys[`${role}Pubkey`] = PrivateKey.from(keys[role]).createPublic().toString();
    });
    setGeneratedKeys(keys);
    setHasDownloadedKeys(false);
  };

  const downloadKeys = () => {
    if (!generatedKeys || !username || !masterKeyRaw) return;
    const text = `Steem Account Details
Username: ${username}
Master Password: ${masterKeyRaw}

============= PRIVATE KEYS =============
Owner:   ${generatedKeys.owner}
Active:  ${generatedKeys.active}
Posting: ${generatedKeys.posting}
Memo:    ${generatedKeys.memo}

============= PUBLIC KEYS =============
Owner:   ${generatedKeys.ownerPubkey}
Active:  ${generatedKeys.activePubkey}
Posting: ${generatedKeys.postingPubkey}
Memo:    ${generatedKeys.memoPubkey}

Please keep these keys safe and DO NOT share your private keys or master password.`;

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Steem keys for @${username}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setHasDownloadedKeys(true);
  };

  const submitOrder = async () => {
    if (!username || !email || !generatedKeys || !captchaText) {
      toast({ title: "Warning", description: "All fields, keys and CAPTCHA are required.", variant: "rose_red" });
      return;
    }
    if (usernameAvailable !== true) {
      toast({ title: "Error", description: "Username is not available or valid.", variant: "rose_red" });
      return;
    }
    setLoading(true);

    try {
      const metadata = {
        owner_pub: generatedKeys.ownerPubkey,
        active_pub: generatedKeys.activePubkey,
        posting_pub: generatedKeys.postingPubkey,
        memo_pub: generatedKeys.memoPubkey
      };

      const data = await create_signup_order({ 
        email, 
        username, 
        metadata, 
        captchaId: captchaData?.id || "", 
        captchaText 
      });
      if (!data.success) {
        if (data.error && data.error.includes("captcha")) fetchCaptcha();
        throw new Error(data.error || "Failed to submit order");
      }
      setResendTimer(60);
      setStep(2);
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Failed to submit order", variant: "rose_red" });
    } finally {
      setLoading(false);
    }
  };

  const submitValidation = async () => {
    if (!validationCode) {
      toast({ title: "Warning", description: "Please enter the validation code", variant: "rose_red" });
      return;
    }
    setLoading(true);
    try {
      const data = await validate_signup_code(validationCode);
      if (!data.success) throw new Error(data.error || "Failed to validate code");
      setStep(3);
      
      // Auto-configure proxy if provided by backend
      if (data.proxy && generatedKeys && generatedKeys.active) {
        setConfiguring(true);
        try {
          await steem.broadcast.accountWitnessProxyAsync(
            generatedKeys.active,
            username,
            data.proxy
          );
        } catch (proxyErr) {
          console.error("Failed to set proxy", proxyErr);
        } finally {
          setConfiguring(false);
        }
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Failed to validate code", variant: "rose_red" });
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (!captchaText) {
      toast({ title: "Warning", description: "Please enter the CAPTCHA code.", variant: "rose_red" });
      return;
    }
    setLoading(true);
    try {
      const data = await resend_signup_code({ 
        email, 
        username, 
        captchaId: captchaData?.id || "", 
        captchaText 
      });
      if (!data.success) {
        if (data.error && data.error.includes("captcha")) fetchCaptcha();
        throw new Error(data.error || "Failed to resend code");
      }
      toast({ title: "Success", description: "Verification code resent. Check your email!", variant: "wild_green" });
      setResendTimer(60);
      fetchCaptcha();
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Failed to resend code", variant: "rose_red" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setUsername("");
    setUsernameAvailable(null);
    setEmail("");
    setGeneratedKeys(null);
    setMasterKeyRaw("");
    setValidationCode("");
    setHasDownloadedKeys(false);
    localStorage.removeItem("steem_signup_order");
  };

  const cancelOrder = async () => {
    if (username && email && step === 2) {
      setLoading(true);
      try {
        await cancel_signup_order(email, username);
      } catch (e) {}
      setLoading(false);
    }
    handleReset();
    hide();
  };

  const handleClose = () => {
    hide();
  };

  const handleGetStarted = () => {
    if (username && generatedKeys && generatedKeys.posting) {
      const autologin = packSessionData({ user: username, password: generatedKeys.posting });
      if (login) login(username, autologin);
    }
    handleReset();
    hide();
    window.location.href = "/";
  };

  const renderStep1 = (
    <>
      <Row className="mb-2">
        <Col md="12">
          <FormGroup>
            <Label>Desired Account Name</Label>
            <div className={`input-icon-group ${focusUser ? 'focused' : ''}`}>
              <i className="fas fa-user input-icon" />
              <input 
                className={`form-control fancy-input ${usernameAvailable === true ? 'is-valid' : usernameAvailable === false ? 'is-invalid' : ''}`}
                placeholder="Username" 
                value={username} 
                onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ''))} 
                onFocus={() => setFocusUser(true)}
                onBlur={() => setFocusUser(false)}
              />
              {checkingUsername && <span className="position-absolute" style={{right: '10px', top: '10px'}}><i className="fas fa-spinner fa-spin text-muted" /></span>}
            </div>
            {usernameAvailable === false && <small className="text-danger">Username already taken or invalid.</small>}
            {usernameAvailable === true && <small className="text-success">Username is available!</small>}
          </FormGroup>
          <FormGroup className="mt-3">
            <Label>Email Address</Label>
            <div className={`input-icon-group ${focusEmail ? 'focused' : ''}`}>
              <i className="fas fa-envelope input-icon" />
              <input 
                type="email" 
                className="form-control fancy-input" 
                placeholder="Email" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                onFocus={() => setFocusEmail(true)}
                onBlur={() => setFocusEmail(false)}
              />
            </div>
          </FormGroup>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md="12">
          <div className="d-flex justify-content-between align-items-center mb-2">
              <Label className="m-0">Steem Keys Generation</Label>
              <Button size="sm" color="primary" disabled={usernameAvailable !== true} onClick={handleGenerateKeys}>Generate Keys</Button>
          </div>
          
          {generatedKeys && (
            <div className="keys-container mt-3 mb-3 p-3 bg-speem-confirm rounded border">
              <Button size="sm" color="success" block onClick={downloadKeys} className="mb-3">
                <i className="fas fa-download" /> Download Keys (TXT)
              </Button>

              <Alert color="warning" className="p-2 mb-3" style={{fontSize: '0.85rem'}}>
                <strong><i className="fas fa-exclamation-triangle" /> Save your keys safely!</strong> We cannot recover them.
              </Alert>

              <Label className="mt-0 mb-0 small font-weight-bold">Master Password</Label>
              <input className="form-control form-control-sm fancy-input mb-3" readOnly value={masterKeyRaw || ""} />

              <Row>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="mb-0 small font-weight-bold">Owner Key</Label>
                    <input className="form-control form-control-sm fancy-input text-danger" readOnly value={generatedKeys.owner} />
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="mb-0 small font-weight-bold">Active Key</Label>
                    <input className="form-control form-control-sm fancy-input text-danger" readOnly value={generatedKeys.active} />
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="mb-0 small font-weight-bold">Posting Key</Label>
                    <input className="form-control form-control-sm fancy-input text-primary" readOnly value={generatedKeys.posting} />
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="mb-0 small font-weight-bold">Memo Key</Label>
                    <input className="form-control form-control-sm fancy-input text-secondary" readOnly value={generatedKeys.memo} />
                  </FormGroup>
                </Col>
              </Row>

              {!hasDownloadedKeys && (
                <div className="text-center mt-2 text-danger font-weight-bold" style={{fontSize: '0.85rem'}}>
                  <i className="fas fa-exclamation-circle"/> You must download your keys to continue.
                </div>
              )}
            </div>
          )}

          {captchaData && (
            <FormGroup className="mt-4 pt-4 border-top">
              <Label className="font-weight-bold">Security Check</Label>
              <Row>
                <Col xs="12" className="mb-2 text-center">
                  <div 
                    dangerouslySetInnerHTML={{ __html: captchaData.svg }} 
                    style={{cursor: 'pointer', background: '#f8f9fa', borderRadius: '8px', overflow: 'hidden', display: 'inline-block'}}
                    onClick={fetchCaptcha}
                    title="Click to refresh captcha"
                  />
                </Col>
                <Col xs="12">
                  <div className={`input-icon-group ${focusCaptcha ? 'focused' : ''}`}>
                    <i className="fas fa-shield-alt input-icon" />
                    <input 
                      className="form-control fancy-input" 
                      placeholder="Enter code" 
                      value={captchaText} 
                      onChange={e => setCaptchaText(e.target.value)} 
                      onFocus={() => setFocusCaptcha(true)}
                      onBlur={() => setFocusCaptcha(false)}
                    />
                  </div>
                </Col>
              </Row>
              <small className="text-muted mt-1 d-block">Click the image if you can't read the code.</small>
            </FormGroup>
          )}
        </Col>
      </Row>
    </>
  );

  const renderStep2 = (
    <div className="text-center py-4">
      <i className="fas fa-envelope fa-3x mb-3 text-speem" />
      <h5>Check your email</h5>
      <p className="text-muted">We have sent a 6-digit validation code to {email}.</p>
      <FormGroup>
        <div className={`input-icon-group ${focusCode ? 'focused' : ''}`}>
          <i className="fas fa-key input-icon" />
          <input 
            className="form-control fancy-input text-center" 
            placeholder="000000" 
            value={validationCode} 
            onChange={e => setValidationCode(e.target.value)} 
            maxLength={6}
            style={{fontSize: "24px", letterSpacing: "4px"}}
            onFocus={() => setFocusCode(true)}
            onBlur={() => setFocusCode(false)}
          />
        </div>
      </FormGroup>
      <div className="mt-4">
        {resendTimer > 0 ? (
          <span className="text-muted small">Resend code in {resendTimer}s</span>
        ) : (
          <>
            {captchaData && (
              <div className="captcha-resend-wrapper mb-3 p-3 bg-speem-confirm rounded border text-left">
                <Label className="small font-weight-bold">Security Check (Resend)</Label>
                <Row className="no-gutters">
                  <Col xs="12" className="mb-2 text-center">
                    <div 
                      dangerouslySetInnerHTML={{ __html: captchaData.svg }} 
                      style={{cursor: 'pointer', background: '#fff', borderRadius: '4px', overflow: 'hidden', display: 'inline-block'}}
                      onClick={fetchCaptcha}
                    />
                  </Col>
                  <Col xs="12">
                    <input 
                      className="form-control form-control-sm fancy-input" 
                      placeholder="Code" 
                      value={captchaText} 
                      onChange={e => setCaptchaText(e.target.value)}
                      style={{height: '40px'}}
                    />
                  </Col>
                </Row>
              </div>
            )}
            <Button size="sm" color="link" disabled={loading} onClick={resendCode}>
               Resend Validation Code
            </Button>
          </>
        )}
      </div>
    </div>
  );

  const renderStep3 = (
    <div className="text-center py-4">
      <i className="fas fa-check-circle fa-4x mb-3 text-success" />
      <h5>Account Created!</h5>
      <p className="text-muted">Your Steem account <strong>{username}</strong> has been created successfully.</p>
      <Alert color="info">
        You can now login using your username and the Master Password you generated.
      </Alert>
      {configuring && (
        <div className="mt-3 text-muted small">
          <i className="fas fa-spinner fa-spin mr-2" /> Configuring your account...
        </div>
      )}
    </div>
  );

  const renderForm = (
    <div className="modal-body post-editor-body mt-0 p-0">
      <Card className="shadow border-0">
        <CardBody className="px-lg-3 pt-0 pb-0 body-gray">
          <div className="relative w-100 flex-shrink-0">
            <div className="post-way empty w-100"></div>
            <div className="post-way filled" style={{position:"absolute", top:0, width: (step/3)*100 + "%"}}></div>
            <div className={`step-name text-muted ${step >= 1?"active":""}`} style={{width: "33.33%"}}>Request</div>
            <div className={`step-name text-muted text-center ${step >= 2?"active":""}`} style={{width: "33.33%"}}>Validate</div>
            <div className={`step-name text-muted text-right ${step === 3?"active":""}`} style={{width: "33.33%"}}>Done</div>
          </div>
          
          <Form className="post-editor-wrapper">
            <div className="post-editor-scroll-area p-3">
              {step === 1 && renderStep1}
              {step === 2 && renderStep2}
              {step === 3 && renderStep3}
            </div>
            <div className="post-editor-buttons p-3 border-top">
              {step === 1 && (
                <>
                  <Button disabled={loading} color="secondary" onClick={() => { handleReset(); hide(); }}>Cancel</Button>
                  <Button disabled={loading || !generatedKeys || !hasDownloadedKeys} color="default" onClick={submitOrder}>
                    {loading ? <span className="spinner" /> : "Continue"}
                  </Button>
                </>
              )}
              {step === 2 && (
                <>
                  <Button disabled={loading} color="secondary" onClick={cancelOrder}>Cancel Order</Button>
                  <Button disabled={loading || validationCode.length < 6} color="default" onClick={submitValidation}>
                    {loading ? <span className="spinner" /> : "Verify Code"}
                  </Button>
                </>
              )}
              {step === 3 && (
                <Button color="default" className="w-100" onClick={handleGetStarted}>Get Started!</Button>
              )}
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );

  return isShowing
    ? ReactDOM.createPortal(
        <AppModal isShowing={isShowing} hide={handleClose} size="md" header="Sign Up">
          {renderForm}
        </AppModal>,
        document.body
      )
    : null;
};
